import React, { useState, useRef } from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  IconButton,
  Typography,
  Button,
  Paper,
  LinearProgress,
  Chip
} from '@mui/material';
import {
  PlayArrow,
  Pause,
  Close,
  VolumeUp,
  VolumeOff,
  Fullscreen,
  Replay
} from '@mui/icons-material';

const DemoVideo = ({ thumbnail, title = "Découvrez LYS Artisans en action" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const videoRef = useRef(null);

  // Pour la démo, on simule une vidéo avec des étapes
  const demoSteps = [
    { time: 0, title: "🏠 Interface Accueil", description: "Découvrez notre tableau de bord intuitif" },
    { time: 15, title: "📋 Gestion Clients", description: "Ajoutez et gérez vos clients en un clic" },
    { time: 30, title: "📅 Planning Intelligent", description: "Organisez vos rendez-vous automatiquement" },
    { time: 45, title: "💰 Devis & Facturation", description: "Créez des devis professionnels en 2 minutes" },
    { time: 60, title: "📊 Analyses & Rapports", description: "Suivez votre activité en temps réel" },
    { time: 75, title: "📱 Application Mobile", description: "Travaillez partout, même hors ligne" }
  ];

  const handlePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      const current = videoRef.current.currentTime;
      const total = videoRef.current.duration;
      setCurrentTime(current);
      setProgress((current / total) * 100);
    }
  };

  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration);
    }
  };

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const getCurrentStep = () => {
    return demoSteps.find((step, index) => {
      const nextStep = demoSteps[index + 1];
      return currentTime >= step.time && (!nextStep || currentTime < nextStep.time);
    }) || demoSteps[0];
  };

  return (
    <>
      {/* Thumbnail Preview */}
      <Paper
        elevation={8}
        sx={{
          position: 'relative',
          borderRadius: 4,
          overflow: 'hidden',
          cursor: 'pointer',
          maxWidth: 600,
          mx: 'auto',
          '&:hover': {
            transform: 'scale(1.02)',
            transition: 'transform 0.3s ease'
          }
        }}
        onClick={() => setIsOpen(true)}
      >
        <Box
          sx={{
            backgroundImage: thumbnail 
              ? `url(${thumbnail})` 
              : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            height: 300,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative'
          }}
        >
          {/* Overlay */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              bgcolor: 'rgba(0,0,0,0.3)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Box sx={{ textAlign: 'center', color: 'white' }}>
              <IconButton
                sx={{
                  bgcolor: 'rgba(255,255,255,0.9)',
                  color: 'primary.main',
                  width: 80,
                  height: 80,
                  mb: 2,
                  '&:hover': {
                    bgcolor: 'white',
                    transform: 'scale(1.1)'
                  }
                }}
              >
                <PlayArrow sx={{ fontSize: 40 }} />
              </IconButton>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
                {title}
              </Typography>
              <Chip 
                label="🎬 Démo 2 minutes" 
                sx={{ 
                  bgcolor: 'rgba(255,255,255,0.9)', 
                  color: 'primary.main',
                  fontWeight: 'bold'
                }} 
              />
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Video Dialog */}
      <Dialog
        open={isOpen}
        onClose={() => setIsOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { 
            bgcolor: 'black',
            borderRadius: 2
          }
        }}
      >
        <DialogContent sx={{ p: 0, position: 'relative' }}>
          {/* Close Button */}
          <IconButton
            onClick={() => setIsOpen(false)}
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              zIndex: 10,
              bgcolor: 'rgba(0,0,0,0.5)',
              color: 'white',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' }
            }}
          >
            <Close />
          </IconButton>

          {/* Demo Video Simulation */}
          <Box sx={{ position: 'relative', bgcolor: 'black', minHeight: 400 }}>
            {/* Fake Video Player */}
            <Box
              sx={{
                height: 400,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundImage: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                position: 'relative'
              }}
            >
              {/* Current Step Display */}
              <Box sx={{ textAlign: 'center', color: 'white', p: 4 }}>
                <Typography variant="h3" gutterBottom>
                  {getCurrentStep().title}
                </Typography>
                <Typography variant="h6" sx={{ opacity: 0.9, mb: 4 }}>
                  {getCurrentStep().description}
                </Typography>
                
                {/* Fake Progress */}
                <Box sx={{ maxWidth: 400, mx: 'auto' }}>
                  <LinearProgress 
                    variant="determinate" 
                    value={progress} 
                    sx={{ 
                      height: 8, 
                      borderRadius: 4,
                      bgcolor: 'rgba(255,255,255,0.3)',
                      '& .MuiLinearProgress-bar': {
                        bgcolor: 'white'
                      }
                    }} 
                  />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                    <Typography variant="caption">
                      {formatTime(currentTime)}
                    </Typography>
                    <Typography variant="caption">
                      {formatTime(90)} {/* 1:30 demo */}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Play/Pause Overlay */}
              {!isPlaying && (
                <IconButton
                  onClick={handlePlayPause}
                  sx={{
                    position: 'absolute',
                    bgcolor: 'rgba(255,255,255,0.9)',
                    color: 'primary.main',
                    width: 80,
                    height: 80,
                    '&:hover': {
                      bgcolor: 'white',
                      transform: 'scale(1.1)'
                    }
                  }}
                >
                  <PlayArrow sx={{ fontSize: 40 }} />
                </IconButton>
              )}
            </Box>

            {/* Controls */}
            <Box
              sx={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                bgcolor: 'rgba(0,0,0,0.7)',
                p: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 2
              }}
            >
              <IconButton onClick={handlePlayPause} sx={{ color: 'white' }}>
                {isPlaying ? <Pause /> : <PlayArrow />}
              </IconButton>
              
              <IconButton onClick={() => setIsMuted(!isMuted)} sx={{ color: 'white' }}>
                {isMuted ? <VolumeOff /> : <VolumeUp />}
              </IconButton>

              <Box sx={{ flexGrow: 1, mx: 2 }}>
                <LinearProgress 
                  variant="determinate" 
                  value={progress} 
                  sx={{ 
                    cursor: 'pointer',
                    '& .MuiLinearProgress-bar': { bgcolor: 'primary.main' }
                  }} 
                />
              </Box>

              <Typography variant="caption" sx={{ color: 'white', minWidth: 80 }}>
                {formatTime(currentTime)} / {formatTime(90)}
              </Typography>

              <IconButton sx={{ color: 'white' }}>
                <Fullscreen />
              </IconButton>
            </Box>
          </Box>

          {/* CTA après la vidéo */}
          <Box sx={{ p: 3, bgcolor: 'white', textAlign: 'center' }}>
            <Typography variant="h6" gutterBottom>
              Prêt à transformer votre activité d'artisan ? 🚀
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 2 }}>
              <Button 
                variant="contained" 
                size="large"
                onClick={() => {
                  setIsOpen(false);
                  window.location.href = '/inscription';
                }}
              >
                🎯 Commencer Gratuitement
              </Button>
              <Button 
                variant="outlined" 
                size="large"
                onClick={() => {
                  setIsOpen(false);
                  window.location.href = '/tarifs';
                }}
              >
                💰 Voir les Tarifs
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Auto-play simulation */}
      {isOpen && isPlaying && (
        <Box sx={{ display: 'none' }}>
          <video
            ref={videoRef}
            onTimeUpdate={handleTimeUpdate}
            onLoadedMetadata={handleLoadedMetadata}
            autoPlay
            muted={isMuted}
          >
            {/* Placeholder - en production, vous ajouteriez votre vraie vidéo */}
            <source src="/demo-video.mp4" type="video/mp4" />
          </video>
        </Box>
      )}
    </>
  );
};

export default DemoVideo;